/**
 * ChronicleStatusWriter — DEV-1352. Author-only modal for composing or editing
 * a chronicle "Current Status" update (Topic Tracker now-state).
 *
 * Deliberately leaner than the Mini-Project StatusWriter: a chronicle status is
 * just a dated narrative + photos. No schedule/budget/goal assessments, and no
 * Journal/feed posting (syndication is a deferred fast-follow).
 *
 * mode="create": new dated update. Reporting date is a native date input,
 *   defaulting to today, capped at today, blocking dates already filed.
 * mode="edit": amend an existing update. Date is locked (slot identifier);
 *   narrative + photos preload.
 *
 * Per CLAUDE.md: opaque overlay (composite base), logical properties, tokens,
 * and the backdrop does NOT dismiss (Skip lost work to accidental dismissals).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import StatusPhotoStrip, { type StatusPhotoItem } from '../StatusPhotoStrip/StatusPhotoStrip';
import {
  createChronicleStatusUpdate,
  updateChronicleStatusUpdate,
  type ChronicleStatusUpdate,
} from '../../api/chronicleStatusUpdates';

interface Props {
  open: boolean;
  onClose: () => void;
  chronicleGuid: string;
  mode: 'create' | 'edit';
  /** Required in edit mode. */
  editingStatus?: ChronicleStatusUpdate | null;
  /** YYYY-MM-DD dates already filed — the create date input rejects these. */
  filedDates: string[];
  /** Fired after a successful create/update. Caller refetches. */
  onSaved: () => void;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatLongDate(iso: string): string {
  try {
    const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const ChronicleStatusWriter: React.FC<Props> = ({
  open,
  onClose,
  chronicleGuid,
  mode,
  editingStatus = null,
  filedDates,
  onSaved,
}) => {
  const { t } = useAppTranslation('pages');
  const isEdit = mode === 'edit' && !!editingStatus;

  const [reportingDate, setReportingDate] = useState<string>(todayIso());
  const [photos, setPhotos] = useState<StatusPhotoItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }),
      Link.configure({ openOnClick: false }),
      Placeholder.configure({
        placeholder: t('chronicleStatus.writer.placeholder', {
          defaultValue: "What's the latest? Describe where things stand right now.",
        }),
      }),
    ],
    content: '',
  });

  // Reset / preload on open transition.
  const prevOpenRef = React.useRef(false);
  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      return;
    }
    if (prevOpenRef.current) return;
    prevOpenRef.current = true;

    setError(null);
    setSaving(false);

    if (isEdit && editingStatus) {
      setReportingDate(editingStatus.reportingDate);
      setPhotos(
        editingStatus.photos.map((p) => ({
          storageKey: p.storageKey,
          caption: p.caption ?? null,
          position: p.position,
        })),
      );
      editor?.commands.setContent(editingStatus.narrativeHtml || '');
    } else {
      setReportingDate(todayIso());
      setPhotos([]);
      editor?.commands.clearContent();
    }
  }, [open, isEdit, editingStatus, editor]);

  const filedSet = useMemo(
    () => new Set(filedDates.map((d) => d)),
    [filedDates],
  );

  const handleSave = useCallback(async () => {
    setError(null);
    const narrative = (editor?.getHTML() || '').trim();
    const narrativeText = (editor?.getText() || '').trim();
    if (!narrativeText) {
      setError(t('chronicleStatus.writer.error.empty', { defaultValue: 'Add some text before posting.' }));
      return;
    }
    if (!isEdit) {
      if (reportingDate > todayIso()) {
        setError(t('chronicleStatus.writer.error.future', { defaultValue: 'The date cannot be in the future.' }));
        return;
      }
      if (filedSet.has(reportingDate)) {
        setError(t('chronicleStatus.writer.error.duplicate', {
          defaultValue: 'An update already exists for that date. Edit it instead.',
        }));
        return;
      }
    }

    setSaving(true);
    try {
      const photosBody = photos.map((p, i) => ({
        storageKey: p.storageKey,
        caption: p.caption ?? null,
        position: i,
      }));
      if (isEdit && editingStatus) {
        await updateChronicleStatusUpdate(chronicleGuid, editingStatus.statusUpdateGuid, {
          narrativeHtml: narrative,
          photos: photosBody,
        });
      } else {
        await createChronicleStatusUpdate(chronicleGuid, {
          reportingDate,
          narrativeHtml: narrative,
          photos: photosBody,
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.body?.error || err?.message || t('chronicleStatus.writer.error.saveFailed', {
        defaultValue: 'Could not save. Please try again.',
      }));
    } finally {
      setSaving(false);
    }
  }, [editor, isEdit, reportingDate, filedSet, photos, editingStatus, chronicleGuid, onSaved, onClose, t]);

  if (!open) return null;

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBlockEnd: 6,
    display: 'block',
  };

  const modal = (
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 24,
        overflowY: 'auto',
        background: 'rgba(2, 6, 23, 0.66)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEdit
          ? t('chronicleStatus.writer.dialogEdit', { defaultValue: 'Edit status update' })
          : t('chronicleStatus.writer.dialogCreate', { defaultValue: 'Post status update' })}
        style={{
          // Opaque composite per CLAUDE.md overlay rule.
          backgroundColor: 'var(--bg-primary)',
          backgroundImage: 'linear-gradient(var(--bg-elevated), var(--bg-elevated))',
          border: '1px solid var(--border-default)',
          borderRadius: 14,
          inlineSize: 'min(620px, 100%)',
          marginBlockStart: 24,
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
        }}
      >
        {/* Header */}
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingBlock: 14,
            paddingInline: 20,
            borderBlockEnd: '1px solid var(--border-subtle)',
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>
            {isEdit
              ? t('chronicleStatus.writer.titleEdit', {
                  defaultValue: 'Edit status — {{date}}',
                  date: formatLongDate(reportingDate),
                })
              : t('chronicleStatus.writer.titleCreate', { defaultValue: 'Post a status update' })}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close', { defaultValue: 'Close' })}
            style={{
              border: 'none', background: 'transparent', color: 'var(--text-secondary)',
              fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: 4,
            }}
          >
            ×
          </button>
        </header>

        {/* Body */}
        <div style={{ paddingBlock: 18, paddingInline: 20, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Reporting date */}
          <div>
            <label style={labelStyle}>
              {t('chronicleStatus.writer.dateLabel', { defaultValue: 'Date' })}
            </label>
            {isEdit ? (
              <span style={{ color: 'var(--text-tertiary)', fontSize: 14 }}>
                {formatLongDate(reportingDate)}
                {' '}
                <span style={{ fontStyle: 'italic' }}>
                  ({t('chronicleStatus.writer.dateLocked', { defaultValue: 'locked' })})
                </span>
              </span>
            ) : (
              <input
                type="date"
                value={reportingDate}
                max={todayIso()}
                onChange={(e) => setReportingDate(e.target.value)}
                style={{
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 8,
                  color: 'var(--text-primary)',
                  padding: '8px 10px',
                  fontSize: 14,
                }}
              />
            )}
          </div>

          {/* Narrative */}
          <div>
            <label style={labelStyle}>
              {t('chronicleStatus.writer.narrativeLabel', { defaultValue: 'Update' })}
            </label>
            <div style={{ display: 'flex', gap: 4, marginBlockEnd: 6 }}>
              <TbBtn active={!!editor?.isActive('bold')} onClick={() => editor?.chain().focus().toggleBold().run()} label="Bold"><b>B</b></TbBtn>
              <TbBtn active={!!editor?.isActive('italic')} onClick={() => editor?.chain().focus().toggleItalic().run()} label="Italic"><i>I</i></TbBtn>
              <TbBtn active={!!editor?.isActive('bulletList')} onClick={() => editor?.chain().focus().toggleBulletList().run()} label="Bullet list">•</TbBtn>
              <TbBtn active={!!editor?.isActive('blockquote')} onClick={() => editor?.chain().focus().toggleBlockquote().run()} label="Quote">"</TbBtn>
              <TbBtn
                active={!!editor?.isActive('link')}
                onClick={() => {
                  const url = window.prompt(t('chronicleStatus.writer.linkPrompt', { defaultValue: 'Link URL' }));
                  if (url) editor?.chain().focus().setLink({ href: url }).run();
                }}
                label="Link"
              >🔗</TbBtn>
            </div>
            <div
              style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-default)',
                borderRadius: 8,
                padding: '10px 12px',
                minBlockSize: 120,
                color: 'var(--text-primary)',
                lineHeight: 1.6,
              }}
            >
              <EditorContent editor={editor} />
            </div>
          </div>

          {/* Photos */}
          <div>
            <label style={labelStyle}>
              {t('chronicleStatus.writer.photosLabel', { defaultValue: 'Photos' })}
              {' '}
              <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--text-tertiary)' }}>
                ({photos.length}/6)
              </span>
            </label>
            <StatusPhotoStrip
              mode="write"
              photos={photos}
              onChange={setPhotos}
              maxPhotos={6}
              uploadBucket="chronicle-status-photos"
              uploadFilePrefix="chronicle-status-photo"
            />
          </div>

          {error && (
            <div style={{ color: 'var(--color-error)', fontSize: 13 }}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <footer
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            paddingBlock: 14,
            paddingInline: 20,
            borderBlockStart: '1px solid var(--border-subtle)',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-default)',
              background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600,
            }}
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: 'var(--accent-primary)', color: 'var(--text-on-accent, #fff)',
              cursor: saving ? 'default' : 'pointer', fontWeight: 700, opacity: saving ? 0.7 : 1,
            }}
          >
            {saving
              ? t('chronicleStatus.writer.saving', { defaultValue: 'Saving…' })
              : isEdit
                ? t('chronicleStatus.writer.saveChanges', { defaultValue: 'Save changes' })
                : t('chronicleStatus.writer.post', { defaultValue: 'Post update' })}
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ChronicleStatusWriter;

/* ── Toolbar button ─────────────────────────────────────────────── */

interface TbBtnProps {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}

function TbBtn({ active, onClick, label, children }: TbBtnProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      style={{
        inlineSize: 30,
        blockSize: 30,
        borderRadius: 6,
        border: '1px solid var(--border-default)',
        background: active ? 'var(--accent-soft, rgba(99,102,241,0.18))' : 'transparent',
        color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
        cursor: 'pointer',
        fontSize: 13,
        lineHeight: 1,
      }}
    >
      {children}
    </button>
  );
}
